/**
 * API Error Handling Utilities
 * 
 * Provides standardized error handling for API responses following
 * the backend's RFC 7807 error format.
 */

/**
 * Custom API Error class that wraps backend error responses
 */
export class ApiError extends Error {
  constructor(errorData = {}) {
    super(errorData.detail || 'An unexpected error occurred');
    this.name = 'ApiError';
    this.code = errorData.code || 'UNKNOWN_ERROR';
    this.status = errorData.status || 500;
    this.title = errorData.title || 'Error';
    this.type = errorData.type || '';
    this.instance = errorData.instance || '';
    this.correlationId = errorData.correlationId || '';
  }

  /**
   * Check if error matches a specific error code
   * @param {string} code - Error code to check (e.g., 'STOCK_NOT_FOUND_001')
   * @returns {boolean}
   */
  isCode(code) {
    return this.code === code;
  }

  /**
   * Check if error code starts with a domain prefix
   * @param {string} domain - Domain prefix (e.g., 'STOCK', 'USER', 'ALGO')
   * @returns {boolean}
   */
  isDomain(domain) {
    return this.code.startsWith(domain);
  }

  /**
   * Check if this is a validation error
   * @returns {boolean}
   */
  isValidationError() {
    return this.code.includes('VALIDATION') || this.status === 400;
  }

  /**
   * Check if this is an authentication error (401)
   * @returns {boolean}
   */
  isAuthError() {
    return this.status === 401;
  }

  /**
   * Check if this is a forbidden/authorization error (403)
   * @returns {boolean}
   */
  isForbiddenError() {
    return this.status === 403;
  }

  /**
   * Check if this is a not found error (404)
   * @returns {boolean}
   */
  isNotFoundError() {
    return this.status === 404 || this.code.includes('NOT_FOUND');
  }

  /**
   * Check if this is a conflict error (409)
   * @returns {boolean}
   */
  isConflictError() {
    return this.status === 409 || this.code.includes('CONFLICT');
  }

  /**
   * Check if this is a server error (5xx)
   * @returns {boolean}
   */
  isServerError() {
    return this.status >= 500;
  }

  /**
   * Check if this is a network error
   * @returns {boolean}
   */
  isNetworkError() {
    return this.code === 'NETWORK_ERROR' || this.status === 0;
  }

  /**
   * Get a loggable representation of the error
   * @returns {Object}
   */
  toLogObject() {
    return {
      code: this.code,
      status: this.status,
      message: this.message,
      title: this.title,
      instance: this.instance,
      correlationId: this.correlationId,
    };
  }
}

/**
 * Extract and normalize error from axios error object
 * @param {Error} error - Axios error or any error object
 * @returns {ApiError}
 */
export const extractApiError = (error) => {
  // Already an ApiError, return as-is
  if (error instanceof ApiError) {
    return error;
  }

  // Handle axios error with new envelope format
  if (error.response?.data?.error) {
    return new ApiError(error.response.data.error);
  }

  // Handle axios error with success=false but error in different format
  if (error.response?.data?.success === false && error.response?.data?.error) {
    return new ApiError(error.response.data.error);
  }

  // Handle legacy error format (message directly in data)
  if (error.response?.data?.message) {
    return new ApiError({
      detail: error.response.data.message,
      status: error.response.status,
      code: `HTTP_${error.response.status}`,
      title: getHttpStatusTitle(error.response.status),
    });
  }

  // Handle network errors (no response)
  if (error.message === 'Network Error' || !error.response) {
    return new ApiError({
      detail: 'Unable to connect to server. Please check your internet connection.',
      status: 0,
      code: 'NETWORK_ERROR',
      title: 'Network Error',
    });
  }

  // Handle timeout errors
  if (error.code === 'ECONNABORTED') {
    return new ApiError({
      detail: 'Request timed out. Please try again.',
      status: 0,
      code: 'TIMEOUT_ERROR',
      title: 'Request Timeout',
    });
  }

  // Fallback for unknown errors
  return new ApiError({
    detail: error.message || 'An unexpected error occurred',
    status: error.response?.status || 500,
    code: 'UNKNOWN_ERROR',
    title: 'Error',
  });
};

/**
 * Get HTTP status title
 * @param {number} status - HTTP status code
 * @returns {string}
 */
const getHttpStatusTitle = (status) => {
  const titles = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    409: 'Conflict',
    422: 'Validation Error',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };
  return titles[status] || 'Error';
};

/**
 * Error code to user-friendly message mapping
 * Add new error codes here as they are defined in the backend
 */
const ERROR_MESSAGES = {
  // Auth/User errors
  USER_AUTH_001: 'Invalid or expired session. Please log in again.',
  USER_AUTH_003: 'Invalid email or password.',
  USER_NOT_FOUND_001: 'User not found.',
  USER_CONFLICT_001: 'This email is already registered.',
  USER_VALIDATION_001: 'Invalid credentials provided.',

  // Stock errors
  STOCK_NOT_FOUND_001: 'Stock instrument not found.',
  STOCK_VALIDATION_001: 'Invalid stock symbol.',
  STOCK_EXTERNAL_001: 'Unable to fetch stock data. Market data service is temporarily unavailable.',

  // Algo/Strategy errors
  ALGO_VALIDATION_001: 'Invalid strategy configuration.',
  ALGO_VALIDATION_002: 'Invalid backtest request.',
  ALGO_NOT_FOUND_001: 'Strategy not found.',
  ALGO_NOT_FOUND_002: 'Backtest result not found.',
  ALGO_FORBIDDEN_001: 'You do not have permission to access this strategy.',
  ALGO_EXTERNAL_001: 'Historical data service is temporarily unavailable.',
  ALGO_CONFLICT_001: 'Strategy is already running.',
  ALGO_INTERNAL_001: 'An error occurred while processing your request.',
  ALGO_INTERNAL_002: 'Backtest execution failed.',

  // Broker errors
  BROKER_NOT_FOUND_001: 'Broker not found.',
  BROKER_EXTERNAL_001: 'Broker service is temporarily unavailable.',

  // Generic errors
  NETWORK_ERROR: 'Unable to connect to server. Please check your internet connection.',
  TIMEOUT_ERROR: 'Request timed out. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
  INTERNAL_ERROR_001: 'An internal error occurred. Please try again later.',
};

/**
 * Get user-friendly error message for an error
 * @param {Error|ApiError} error - Error object
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  if (error instanceof ApiError) {
    // Check for specific error code message
    if (ERROR_MESSAGES[error.code]) {
      return ERROR_MESSAGES[error.code];
    }
    // Fall back to the error's detail message
    return error.message;
  }

  // For non-ApiError, just return the message
  return error.message || 'An unexpected error occurred';
};

/**
 * Get error severity level for UI display
 * @param {ApiError} error - API error object
 * @returns {'error'|'warning'|'info'} Severity level
 */
export const getErrorSeverity = (error) => {
  if (!(error instanceof ApiError)) {
    return 'error';
  }

  // Validation errors are warnings (user can fix)
  if (error.isValidationError()) {
    return 'warning';
  }

  // Not found can be informational
  if (error.isNotFoundError()) {
    return 'info';
  }

  // Everything else is an error
  return 'error';
};

export default ApiError;
